import type { UserRole } from "@/domain/auth/role";

/**
 * Claims the Supabase access-token hook (`custom_access_token_hook`) adds to
 * every access token: which tenant the user belongs to, their role and
 * company in it, and whether they are a platform (super) admin.
 */
export type AppClaims = {
  isPlatformAdmin: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
  role: UserRole | null;
  companyId: number | null;
};

const ROLES: readonly UserRole[] = ["driver", "company", "admin"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Decodes the payload (middle segment) of a JWT. No signature check — the
 * token was just issued to this browser by Supabase, and everything that
 * matters is enforced server-side. Returns `null` for anything malformed.
 */
export function decodeJwtPayload(token: string): unknown {
  const segment = token.split(".")[1];
  if (!segment) {
    return null;
  }
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  } catch {
    return null;
  }
}

/**
 * Narrows a decoded token payload to `AppClaims`. Returns `null` when the
 * token carries none of our claims (hook not enabled yet, or a token issued
 * before it was) — callers refresh the session once and treat a second
 * `null` as "no access".
 */
export function parseClaims(payload: unknown): AppClaims | null {
  if (!isRecord(payload)) {
    return null;
  }
  const role = ROLES.find((r) => r === payload.app_role) ?? null;
  const claims: AppClaims = {
    isPlatformAdmin: payload.is_platform_admin === true,
    tenantId: asString(payload.tenant_id),
    tenantSlug: asString(payload.tenant_slug),
    role,
    companyId: typeof payload.company_id === "number" ? payload.company_id : null,
  };
  if (!("is_platform_admin" in payload) && claims.role === null && claims.tenantId === null) {
    return null;
  }
  return claims;
}
